import { genzoTrackImpression } from '@alexis/helpers/genzo';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import type { FlightSortType } from 'flights/flightSortType';
import type { TripViewModel } from 'flights/tripViewModel';
import { useEffect, useState, useRef, useCallback } from 'react';
import type { SortOrder } from 'sortOrder';

import type { User } from '@wegoTypes/user';

export interface FlightSearchResultImpressionLogging {
  handlePageLoad(): void;
  handleSortOptionChange(eventId: string): void;
  handleFilterOptionChange(eventId: string): void;
  handleSimilarOptionClick(): void;
  handleTripCardClick(tripViewModel: TripViewModel): void;
}

export function useFlightSearchResultImpressionLogging(
  appliedFilters: string,
  clientId: string | undefined,
  clientSessionId: string | undefined,
  groupedTripViewModelsDetailWithInlineAds: Array<{
    key: string;
    index: number;
    maxLength: number;
    tripViewModel?: TripViewModel;
  }>,
  selectedTripSortType: FlightSortType,
  selectedSortOrder: SortOrder,
  pageViewId: string,
  sortedAndFilteredTripViewModelsWithInlineAds: Array<TripViewModel>,
  user: User | null,
): FlightSearchResultImpressionLogging {
  const { search } = useFlightSearchResults();
  const { isPerLegSearch } = useFlightPLS();

  const [impressionLogOnPageLoad, setImpressionLogOnPageLoad] = useState<boolean>(false);

  const [impressionLogOnSort, setImpressionLogOnSort] = useState<boolean>(false);
  const [sortEventId, setSortEventId] = useState<string | null>(null);

  let filterDebounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [impressionLogOnFilter, setImpressionLogOnFilter] = useState<boolean>(false);
  const [filterEventId, setFilterEventId] = useState<string | null>(null);

  const [impressionLogOnSimilarOptionsClick, setImpressionLogOnSimilarOptionsClick] =
    useState<boolean>(false);

  const [impressionLogOnTripCardClick, setImpressionLogOnTripCardClick] = useState<boolean>(false);
  const [clickedTripViewModel, setClickedTripViewModel] = useState<TripViewModel>();

  const getSortTypeString = (sortType: FlightSortType, sortOrder: SortOrder) => {
    switch (sortType) {
      case 'price':
        return 'cheapest';
      case 'duration':
        return 'fastest';
      case 'score':
        return 'recommended';
      case 'leg1_departure_time':
      case 'leg2_departure_time':
      case 'leg3_departure_time':
      case 'leg4_departure_time':
      case 'leg5_departure_time':
      case 'leg6_departure_time': {
        const order = sortOrder === 'asc' ? 'earliest' : 'latest';
        return `leg${sortType.match(/\d+/g)![0]}_departure_${order}`;
      }
    }
  };

  const handlePageLoad = useCallback(() => {
    if (!!search && !isPerLegSearch) {
      setTimeout(() => {
        setImpressionLogOnPageLoad(true);
      }, 1000);
    }
  }, [isPerLegSearch, search]);

  useEffect(() => {
    window.addEventListener('onPageLoadLogImpression', handlePageLoad);

    return () => {
      window.removeEventListener('onPageLoadLogImpression', handlePageLoad);
    };
  }, [handlePageLoad]);

  const handleSortOptionChange = (eventId: string) => {
    if (isPerLegSearch) {
      return;
    }

    // Log impression after 1s
    setSortEventId(eventId);
    setTimeout(() => {
      setImpressionLogOnSort(true);
    }, 1000);
  };

  const handleFilterOptionChange = (eventId: string) => {
    if (isPerLegSearch) {
      return;
    }

    // Log impression with debouce of 1s
    setFilterEventId(eventId);

    if (!!filterDebounceTimeoutRef.current) {
      clearTimeout(filterDebounceTimeoutRef.current);
    }

    filterDebounceTimeoutRef.current = setTimeout(() => {
      setImpressionLogOnFilter(true);
    }, 1000);
  };

  const handleSimilarOptionClick = () => {
    if (isPerLegSearch) {
      return;
    }

    setTimeout(() => {
      setImpressionLogOnSimilarOptionsClick(true);
    }, 1000);
  };

  const handleTripCardClick = (tripViewModel: TripViewModel) => {
    if (isPerLegSearch) {
      return;
    }

    setClickedTripViewModel(tripViewModel);
    setImpressionLogOnTripCardClick(true);
  };

  // Impression log on Card Click
  useEffect(() => {
    if (
      !!search?.id &&
      !!clientId &&
      !!clientSessionId &&
      !!impressionLogOnTripCardClick &&
      !!clickedTripViewModel
    ) {
      setImpressionLogOnTripCardClick(false);

      let tripViewModelsArray: Array<TripViewModel | undefined> = [];
      if (selectedTripSortType === 'price') {
        tripViewModelsArray = groupedTripViewModelsDetailWithInlineAds.map(
          (groupedTripViewModel) => groupedTripViewModel.tripViewModel,
        );
      } else {
        tripViewModelsArray = sortedAndFilteredTripViewModelsWithInlineAds;
      }

      const order = tripViewModelsArray
        .filter((tripViewModel) => !tripViewModel?.isPlaceholder)
        .findIndex(
          (tripViewModelDetail) => tripViewModelDetail?.tripId === clickedTripViewModel?.tripId,
        );

      const clickedIndex = tripViewModelsArray.findIndex(
        (tripViewModelDetail) => tripViewModelDetail?.tripId === clickedTripViewModel?.tripId,
      );

      const isAdAbove = tripViewModelsArray[clickedIndex - 1]?.isPlaceholder;
      const isNativeAdAbove = tripViewModelsArray[clickedIndex - 1]?.isNativeAd;
      const ad_above = isNativeAdAbove ? 'native' : isAdAbove ? 'banner' : 'no_ad';

      const orderLabel: string[] = [...new Set(clickedTripViewModel.tripCardTags)];

      if (orderLabel.includes('cheapest') && orderLabel.includes('cheapest_direct')) {
        const indexOfCheapestDirect = orderLabel.indexOf('cheapest_direct');
        orderLabel.splice(indexOfCheapestDirect, 1);
      }

      const objects = [
        {
          id: clickedTripViewModel.tripId,
          itinerary_category: clickedTripViewModel.sponsored ? 'sponsored' : 'meta',
          order: order + 1,
          order_label: orderLabel.length ? orderLabel.join(', ') : null,
          cheapest_price_usd: clickedTripViewModel.filteredFares[0]?.fare.price.amountUsd,
          clicked_price_usd: clickedTripViewModel.filteredFares[0]?.fare.price.amountUsd,
          num_providers: clickedTripViewModel.filteredFares.length,
          top_provider: clickedTripViewModel.filteredFares[0]?.fare.providerCode,
          clicked_provider: clickedTripViewModel.filteredFares[0]?.fare.providerCode,
          ad_above: ad_above,
          filtered: appliedFilters,
          experience_score: clickedTripViewModel.filteredFares[0]?.fare.score,
        },
      ];

      const sortTypeString: string = getSortTypeString(selectedTripSortType, selectedSortOrder);

      genzoTrackImpression(
        'click',
        API_BASE_URL,
        clientId,
        clientSessionId,
        null,
        objects,
        'flights_search_results',
        pageViewId,
        search.id,
        sortTypeString,
        user?.userHash,
      );
    }
  }, [
    appliedFilters,
    clickedTripViewModel,
    clientId,
    clientSessionId,
    search?.id,
    groupedTripViewModelsDetailWithInlineAds,
    impressionLogOnTripCardClick,
    pageViewId,
    selectedTripSortType,
    selectedSortOrder,
    sortedAndFilteredTripViewModelsWithInlineAds,
    user,
  ]);

  // Impression log on page load, sort, filter and fare group click events
  useEffect(() => {
    if (
      !!search?.id &&
      !!clientId &&
      !!clientSessionId &&
      (impressionLogOnPageLoad ||
        impressionLogOnSort ||
        impressionLogOnFilter ||
        impressionLogOnSimilarOptionsClick)
    ) {
      let impressionTrigger = '';
      let event_id: string | null = null;

      let objects: {
        id: string;
        itinerary_category: string;
        order: number;
        cheapest_price_usd: number;
        num_providers: number;
        top_provider: string;
        ad_above: string;
        filtered: string;
        experience_score: number;
      }[] = [];

      let tripViewModelsArray: Array<TripViewModel | undefined> = [];
      if (selectedTripSortType === 'price') {
        tripViewModelsArray = groupedTripViewModelsDetailWithInlineAds.map(
          (groupedTripViewModel) => groupedTripViewModel.tripViewModel,
        );
      } else {
        tripViewModelsArray = sortedAndFilteredTripViewModelsWithInlineAds;
      }

      if (impressionLogOnPageLoad) {
        setImpressionLogOnPageLoad(false);
        impressionTrigger = 'page_load';
      } else if (impressionLogOnSort) {
        setImpressionLogOnSort(false);
        impressionTrigger = 'sort';
        event_id = sortEventId;
      } else if (impressionLogOnFilter) {
        setImpressionLogOnFilter(false);
        impressionTrigger = 'filter';
        event_id = filterEventId;
      } else if (impressionLogOnSimilarOptionsClick) {
        setImpressionLogOnSimilarOptionsClick(false);
        impressionTrigger = 'faregroup_interaction';
      }

      const noOfTripViewModelsToSlice =
        tripViewModelsArray.slice(0, 25).filter((tripViewModel) => tripViewModel?.isPlaceholder)
          .length + 25;

      tripViewModelsArray.slice(0, noOfTripViewModelsToSlice).map((tripViewModel, index) => {
        if (!!tripViewModel && !tripViewModel.isPlaceholder) {
          const isAdAbove = tripViewModelsArray[index - 1]?.isPlaceholder;
          const isNativeAdAbove = tripViewModelsArray[index - 1]?.isNativeAd;
          const ad_above = isNativeAdAbove ? 'native' : isAdAbove ? 'banner' : 'no_ad';

          const cheapestFareViewModel = tripViewModel.filteredFares[0];

          const orderLabel: string[] = [...new Set(tripViewModel.tripCardTags)];

          if (orderLabel.includes('cheapest') && orderLabel.includes('cheapest_direct')) {
            const indexOfCheapestDirect = orderLabel.indexOf('cheapest_direct');
            orderLabel.splice(indexOfCheapestDirect, 1);
          }

          const object = {
            id: tripViewModel.tripId,
            itinerary_category: tripViewModel.sponsored ? 'sponsored' : 'meta',
            order: index + 1,
            order_label: orderLabel.length ? orderLabel.join(', ') : null,
            cheapest_price_usd: cheapestFareViewModel?.fare?.price?.amountUsd,
            clicked_price_usd: null,
            num_providers: tripViewModel.filteredFares.length,
            top_provider: cheapestFareViewModel?.provider?.code,
            clicked_provider: null,
            ad_above: ad_above,
            filtered: appliedFilters,
            experience_score: cheapestFareViewModel?.fare?.score,
          };

          objects.push(object);
        }
      });

      const sortTypeString: string = getSortTypeString(selectedTripSortType, selectedSortOrder);

      genzoTrackImpression(
        impressionTrigger,
        API_BASE_URL,
        clientId!,
        clientSessionId!,
        event_id,
        objects,
        'flights_search_results',
        pageViewId,
        search!.id,
        sortTypeString,
        user?.userHash,
      );
    }
  }, [
    appliedFilters,
    clientId,
    clientSessionId,
    search?.id,
    filterEventId,
    groupedTripViewModelsDetailWithInlineAds,
    impressionLogOnPageLoad,
    impressionLogOnSort,
    impressionLogOnFilter,
    impressionLogOnSimilarOptionsClick,
    pageViewId,
    selectedTripSortType,
    sortedAndFilteredTripViewModelsWithInlineAds,
    sortEventId,
    user,
  ]);

  const useFlightSearchResultImpressionLogging = {
    handlePageLoad,
    handleSortOptionChange,
    handleFilterOptionChange,
    handleSimilarOptionClick,
    handleTripCardClick,
  };

  return useFlightSearchResultImpressionLogging;
}
